import { Code } from '@gravity-ui/icons';
import { useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import PageContext from '@/Providers/PageContextProvider/PageContext';

const visibilityRelatedStyles: Record<string, string> = Object.freeze({
  visibility: 'visible',
  height: 'unset',
  width: 'unset',
  transform: 'unset',
  opacity: 'unset',
  position: 'static',
});

const buildPermanentVisibleStyles = (styles: Record<string, string>) => ({
  ...visibilityRelatedStyles,
  ...styles,
});

/** Inline `!important` so a tenant's custom CSS cannot hide the banner. */
const applyInlineStylesWithImportant = (element: HTMLElement, styles: Record<string, string>) => {
  for (const [key, value] of Object.entries(styles)) {
    element.style.setProperty(key, value, 'important');
  }
};

const styles: Record<string, string> = Object.freeze({
  display: 'flex',
  'align-items': 'center',
  'justify-content': 'center',
  'align-self': 'stretch',
  gap: '8px',
  padding: '6px 16px',
  'padding-top': 'max(6px, env(safe-area-inset-top))',
  top: '0',
  'border-radius': 'unset',
  background: 'var(--warning)',
  color: 'var(--warning-foreground)',
  'font-size': '0.75rem',
  'line-height': '1rem',
  'font-weight': '500',
  position: 'fixed',
  'inset-inline-start': '0',
  'inset-inline-end': '0',
  // Dev tenants only: the banner deliberately sits above modals and toasts.
  'z-index': '9999',
});

const iconStyles: Record<string, string> = Object.freeze({
  display: 'inline-flex',
  'align-items': 'center',
});

/** Shown for development tenants, except in preview mode. */
const DevelopmentTenantNotification = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLSpanElement>(null);
  const { experienceSettings, isPreview } = useContext(PageContext);
  const { isDevelopmentTenant } = experienceSettings ?? {};

  useEffect(() => {
    if (isPreview || !ref.current || !iconRef.current) {
      return;
    }

    applyInlineStylesWithImportant(ref.current, buildPermanentVisibleStyles(styles));
    applyInlineStylesWithImportant(iconRef.current, buildPermanentVisibleStyles(iconStyles));
  }, [isDevelopmentTenant, isPreview]);

  if (!isDevelopmentTenant || isPreview) {
    return null;
  }

  return createPortal(
    <div ref={ref}>
      <span ref={iconRef}>
        <Code aria-hidden width={16} height={16} />
      </span>
      {t('development_tenant.notification')}
    </div>,
    document.body
  );
};

export default DevelopmentTenantNotification;
